import React from 'react';

/**
 * Haptic Feedback Utility for Mobile Devices
 * Provides tactile feedback for touch interactions
 * Special handling for iOS devices
 */

// Check if device is iOS
const isIOSDevice = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Check if device is Android
const isAndroidDevice = () => {
  return /Android/i.test(navigator.userAgent);
};

// Check if device supports vibration
const isVibrationSupported = () => {
  return 'vibrate' in navigator || 'webkitVibrate' in navigator;
};

// Check if device is mobile (to avoid vibration on desktop)
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

// iOS-specific haptic feedback using Web API
const triggerIOSHaptic = (intensity = 'medium') => {
  // iOS Safari 14.5+ supports Haptic Feedback API
  if ('vibrate' in navigator) {
    // For iOS, use specific patterns that work better
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [50],
      success: [10, 50, 10],
      error: [50, 100, 50],
      warning: [25, 25, 25]
    };
    
    navigator.vibrate(patterns[intensity] || patterns.medium);
    return true;
  }
  
  // Fallback: Try to trigger through user interaction simulation
  if (window.DeviceMotionEvent && typeof DeviceMotionEvent.requestPermission === 'function') {
    // This doesn't create haptics but ensures user interaction context
    console.log('iOS Haptic feedback attempted');
  }
  
  return false;
};

// Vibration patterns for different interactions
const VibrationPatterns = {
  // Light tap for button presses
  LIGHT_TAP: [10],
  
  // Medium tap for selections and toggles
  MEDIUM_TAP: [20],
  
  // Success feedback
  SUCCESS: [10, 50, 10],
  
  // Error feedback
  ERROR: [100, 50, 100],
  
  // Swipe gesture feedback
  SWIPE: [15],
  
  // Long press feedback
  LONG_PRESS: [50],
  
  // Selection feedback
  SELECT: [25],
  
  // Navigation feedback
  NAVIGATION: [12]
};



// Visual feedback as alternative to vibration
const triggerVisualFeedback = (pattern = 'LIGHT_TAP') => {
  try {
    // Create a temporary visual feedback element
    const feedbackElement = document.createElement('div');
    feedbackElement.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
      background: rgba(0, 123, 255, 0.6);
      border-radius: 50%;
      pointer-events: none;
      z-index: 10000;
      animation: haptic-pulse 0.3s ease-out;
    `;
    
    // Add CSS animation if not already present
    if (!document.getElementById('haptic-feedback-styles')) {
      const style = document.createElement('style');
      style.id = 'haptic-feedback-styles';
      style.textContent = `
        @keyframes haptic-pulse {
          0% { 
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0.8;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.4;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(feedbackElement);
    
    // Remove element after animation
    setTimeout(() => {
      if (feedbackElement.parentNode) {
        feedbackElement.parentNode.removeChild(feedbackElement);
      }
    }, 300);
    
    return true;
  } catch (error) {
    console.debug('Visual feedback failed:', error);
    return false;
  }
};

/**
 * Trigger haptic feedback with comprehensive fallbacks
 * @param {string} pattern - Pattern name from VibrationPatterns
 * @param {boolean} force - Force feedback even if conditions aren't met
 * @param {Object} options - Additional options for feedback
 */
export const triggerHapticFeedback = (pattern = 'LIGHT_TAP', force = false, options = {}) => {
  const {
    enableAudioFallback = true,
    enableVisualFallback = false,
    enableConsoleLog = false
  } = options;
  
  // Only provide feedback on mobile devices unless forced
  if (!force && !isMobileDevice()) {
    if (enableConsoleLog) console.debug('Haptic feedback skipped - not mobile device');
    return;
  }
  
  let feedbackProvided = false;
  
  // Special handling for iOS devices
  if (isIOSDevice()) {
    if (enableConsoleLog) console.log('iOS haptic feedback triggered:', pattern);
    
    // Map patterns to iOS-friendly intensities
    const iosIntensityMap = {
      'LIGHT_TAP': 'light',
      'MEDIUM_TAP': 'medium', 
      'SUCCESS': 'success',
      'ERROR': 'error',
      'SWIPE': 'light',
      'SELECT': 'medium',
      'NAVIGATION': 'light',
      'LONG_PRESS': 'heavy'
    };
    
    const intensity = iosIntensityMap[pattern] || 'medium';
    
    // Try multiple methods for iOS
    
    // Method 1: Direct vibration API
    if (!feedbackProvided && navigator.vibrate) {
      try {
        const vibrationPattern = VibrationPatterns[pattern] || VibrationPatterns.LIGHT_TAP;
        const result = navigator.vibrate(vibrationPattern);
        if (result !== false) {
          feedbackProvided = true;
          if (enableConsoleLog) console.debug('iOS vibration API succeeded');
        }
      } catch (error) {
        if (enableConsoleLog) console.debug('iOS vibrate API failed:', error);
      }
    }
    
    // Method 2: WebKit vibration API
    if (!feedbackProvided && navigator.webkitVibrate) {
      try {
        const vibrationPattern = VibrationPatterns[pattern] || VibrationPatterns.LIGHT_TAP;
        const result = navigator.webkitVibrate(vibrationPattern);
        if (result !== false) {
          feedbackProvided = true;
          if (enableConsoleLog) console.debug('iOS webkit vibration succeeded');
        }
      } catch (error) {
        if (enableConsoleLog) console.debug('iOS webkit vibrate failed:', error);
      }
    }
    
    // Method 3: iOS-specific haptic feedback
    if (!feedbackProvided && triggerIOSHaptic(intensity)) {
      feedbackProvided = true;
      if (enableConsoleLog) console.debug('iOS haptic feedback succeeded');
    }
    
    // Fallback methods for iOS
    if (!feedbackProvided) {
      if (enableConsoleLog) console.debug('iOS vibration not supported, trying fallbacks');
      
      // Visual feedback fallback
      if (enableVisualFallback && triggerVisualFeedback(pattern)) {
        feedbackProvided = true;
        if (enableConsoleLog) console.debug('iOS visual feedback provided');
      }
    }
    
    return feedbackProvided;
  }
  
  // Android and other devices
  if (isVibrationSupported()) {
    try {
      const vibrationPattern = VibrationPatterns[pattern] || VibrationPatterns.LIGHT_TAP;
      
      // Use navigator.vibrate with fallback to webkitVibrate
      if (navigator.vibrate) {
        const result = navigator.vibrate(vibrationPattern);
        if (result !== false) {
          feedbackProvided = true;
          if (enableConsoleLog) console.debug('Standard vibration API succeeded');
        }
      } else if (navigator.webkitVibrate) {
        const result = navigator.webkitVibrate(vibrationPattern);
        if (result !== false) {
          feedbackProvided = true;
          if (enableConsoleLog) console.debug('WebKit vibration API succeeded');
        }
      }
    } catch (error) {
      if (enableConsoleLog) console.debug('Vibration API failed:', error);
    }
  }
  
  // Fallback methods for non-iOS devices without vibration support
  if (!feedbackProvided) {
    if (enableConsoleLog) console.debug('Vibration API not supported, trying fallbacks');
    
    // Visual feedback fallback
    if (enableVisualFallback && triggerVisualFeedback(pattern)) {
      feedbackProvided = true;
      if (enableConsoleLog) console.debug('Visual feedback provided');
    }
  }
  
  if (!feedbackProvided && enableConsoleLog) {
    console.debug('No haptic feedback method available');
  }
  
  return feedbackProvided;
};

/**
 * Haptic feedback for button interactions with fallback options
 */
export const hapticFeedback = {
  // Light feedback for regular button presses
  buttonPress: (options = {}) => triggerHapticFeedback('LIGHT_TAP', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: false,
    ...options 
  }),
  
  // Medium feedback for important actions
  importantAction: (options = {}) => triggerHapticFeedback('MEDIUM_TAP', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: true,
    ...options 
  }),
  
  // Success feedback for completed actions
  success: (options = {}) => triggerHapticFeedback('SUCCESS', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: true,
    ...options 
  }),
  
  // Error feedback for failed actions
  error: (options = {}) => triggerHapticFeedback('ERROR', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: true,
    ...options 
  }),
  
  // Swipe gesture feedback
  swipe: (options = {}) => triggerHapticFeedback('SWIPE', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: false,
    ...options 
  }),
  
  // Selection feedback
  select: (options = {}) => triggerHapticFeedback('SELECT', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: false,
    ...options 
  }),
  
  // Navigation feedback
  navigate: (options = {}) => triggerHapticFeedback('NAVIGATION', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: false,
    ...options 
  }),
  
  // Long press feedback
  longPress: (options = {}) => triggerHapticFeedback('LONG_PRESS', false, { 
    enableAudioFallback: false, 
    enableVisualFallback: true,
    ...options 
  }),
  
  // Test function to verify feedback is working
  test: (pattern = 'MEDIUM_TAP', options = {}) => triggerHapticFeedback(pattern, true, {
    enableAudioFallback: false,
    enableVisualFallback: true,
    enableConsoleLog: true,
    ...options
  })
};

/**
 * Initialize haptic feedback for iOS devices
 * Must be called from a user interaction event
 */
export const initializeIOSHaptics = async () => {
  if (!isIOSDevice()) return true;
  
  try {
    // Request device motion permission for iOS 13+
    if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
      const permission = await DeviceMotionEvent.requestPermission();
      console.log('iOS DeviceMotion permission:', permission);
      return permission === 'granted';
    }
    
    // For older iOS versions, try a test vibration
    if (navigator.vibrate) {
      const testResult = navigator.vibrate(1);
      console.log('iOS vibration test result:', testResult);
      return testResult !== false;
    }
    
    return true;
  } catch (error) {
    console.warn('iOS haptic initialization failed:', error);
    return false;
  }
};

// Check what feedback methods are available
const getAvailableFeedbackMethods = () => {
  const methods = {
    vibration: false,
    webkitVibration: false,
    visual: true // Visual feedback is always available
  };
  
  // Check vibration support
  if ('vibrate' in navigator) {
    try {
      // Test with a very short vibration
      const result = navigator.vibrate(1);
      methods.vibration = result !== false;
    } catch (error) {
      methods.vibration = false;
    }
  }
  
  // Check webkit vibration support
  if ('webkitVibrate' in navigator) {
    try {
      const result = navigator.webkitVibrate(1);
      methods.webkitVibration = result !== false;
    } catch (error) {
      methods.webkitVibration = false;
    }
  }
  
  return methods;
};

/**
 * React Hook for haptic feedback integration with comprehensive fallback detection
 */
export const useHapticFeedback = () => {
  const isMobile = isMobileDevice();
  const isSupported = isVibrationSupported();
  const isIOS = isIOSDevice();
  const isAndroid = isAndroidDevice();
  
  // Initialize iOS haptics on first user interaction
  const [iosInitialized, setIOSInitialized] = React.useState(false);
  const [availableMethods, setAvailableMethods] = React.useState(null);
  const [fallbackMethodsEnabled, setFallbackMethodsEnabled] = React.useState({
    visual: false
  });
  
  // Check available feedback methods
  React.useEffect(() => {
    const methods = getAvailableFeedbackMethods();
    setAvailableMethods(methods);
    console.log('Available haptic feedback methods:', methods);
  }, []);
  
  const initializeIOS = React.useCallback(async () => {
    if (isIOS && !iosInitialized) {
      const success = await initializeIOSHaptics();
      setIOSInitialized(success);
      console.log('iOS haptics initialized:', success);
      return success;
    }
    return iosInitialized;
  }, [isIOS, iosInitialized]);
  
  // Auto-initialize on first touch event
  React.useEffect(() => {
    if (isIOS && !iosInitialized) {
      const handleFirstTouch = () => {
        initializeIOS();
        document.removeEventListener('touchstart', handleFirstTouch);
        document.removeEventListener('click', handleFirstTouch);
      };
      
      document.addEventListener('touchstart', handleFirstTouch, { once: true });
      document.addEventListener('click', handleFirstTouch, { once: true });
      
      return () => {
        document.removeEventListener('touchstart', handleFirstTouch);
        document.removeEventListener('click', handleFirstTouch);
      };
    }
  }, [isIOS, iosInitialized, initializeIOS]);
  
  // Enhanced feedback functions with automatic fallback handling
  const enhancedFeedback = React.useMemo(() => {
    const createFeedbackFunction = (originalFn, defaultOptions = {}) => {
      return (options = {}) => {
        const mergedOptions = {
          enableAudioFallback: false,
          enableVisualFallback: fallbackMethodsEnabled.visual,
          ...defaultOptions,
          ...options
        };
        return originalFn(mergedOptions);
      };
    };
    
    return {
      buttonPress: createFeedbackFunction(hapticFeedback.buttonPress),
      importantAction: createFeedbackFunction(hapticFeedback.importantAction, { enableVisualFallback: true }),
      success: createFeedbackFunction(hapticFeedback.success, { enableVisualFallback: true }),
      error: createFeedbackFunction(hapticFeedback.error, { enableVisualFallback: true }),
      swipe: createFeedbackFunction(hapticFeedback.swipe),
      select: createFeedbackFunction(hapticFeedback.select),
      navigate: createFeedbackFunction(hapticFeedback.navigate),
      longPress: createFeedbackFunction(hapticFeedback.longPress, { enableVisualFallback: true }),
      test: createFeedbackFunction(hapticFeedback.test, { 
        enableVisualFallback: true,
        enableConsoleLog: true 
      })
    };
  }, [fallbackMethodsEnabled]);
  
  return {
    ...enhancedFeedback,
    
    // Device and support information
    isSupported: isSupported && isMobile,
    isMobile,
    isIOS,
    isAndroid,
    iosInitialized,
    availableMethods,
    
    // Control functions
    initializeIOS,
    setFallbackMethodsEnabled,
    fallbackMethodsEnabled,
    
    // Test function to check what works
    testAllMethods: () => {
      console.log('Testing all haptic feedback methods...');
      const results = {};
      
      // Test vibration
      results.vibration = triggerHapticFeedback('MEDIUM_TAP', true, {
        enableAudioFallback: false,
        enableVisualFallback: false,
        enableConsoleLog: true
      });
      
      // Test visual
      results.visual = triggerVisualFeedback('MEDIUM_TAP');
      
      console.log('Haptic feedback test results:', results);
      return results;
    }
  };
};

/**
 * Get a human-readable status of haptic feedback capabilities
 */
export const getHapticFeedbackStatus = () => {
  const isMobile = isMobileDevice();
  const isIOS = isIOSDevice();
  const isAndroid = isAndroidDevice();
  const isVibrationSupported = 'vibrate' in navigator || 'webkitVibrate' in navigator;
  const methods = getAvailableFeedbackMethods();
  
  let status = {
    device: 'Unknown',
    vibrationSupported: false,
    fallbacksAvailable: [],
    recommendedSettings: {},
    message: ''
  };
  
  if (isIOS) {
    status.device = 'iOS';
    status.vibrationSupported = methods.vibration || methods.webkitVibration;
    status.fallbacksAvailable = [];
    
    if (methods.visual) status.fallbacksAvailable.push('Visual');
    
    status.recommendedSettings = {
      enableAudioFallback: false,
      enableVisualFallback: false
    };
    
    if (status.vibrationSupported) {
      status.message = 'iOS device with vibration support detected.';
    } else {
      status.message = 'iOS device without vibration support. Visual fallback available.';
    }
  } else if (isAndroid) {
    status.device = 'Android';
    status.vibrationSupported = methods.vibration || methods.webkitVibration;
    status.fallbacksAvailable = [];
    
    if (methods.visual) status.fallbacksAvailable.push('Visual');
    
    status.recommendedSettings = {
      enableAudioFallback: false,
      enableVisualFallback: false
    };
    
    if (status.vibrationSupported) {
      status.message = 'Android device with vibration support detected.';
    } else {
      status.message = 'Android device without vibration support. Visual fallback available.';
    }
  } else if (isMobile) {
    status.device = 'Mobile (Unknown)';
    status.vibrationSupported = methods.vibration || methods.webkitVibration;
    status.fallbacksAvailable = [];
    
    if (methods.visual) status.fallbacksAvailable.push('Visual');
    
    status.recommendedSettings = {
      enableAudioFallback: false,
      enableVisualFallback: false
    };
    
    status.message = status.vibrationSupported 
      ? 'Mobile device with vibration support detected.'
      : 'Mobile device without vibration support. Visual fallback available.';
  } else {
    status.device = 'Desktop';
    status.vibrationSupported = false;
    status.fallbacksAvailable = [];
    
    if (methods.visual) status.fallbacksAvailable.push('Visual');
    
    status.recommendedSettings = {
      enableAudioFallback: false,
      enableVisualFallback: true
    };
    
    status.message = 'Desktop device detected. Visual feedback available for testing.';
  }
  
  return status;
};

/**
 * Auto-configure haptic feedback based on device capabilities
 */
export const autoConfigureHapticFeedback = () => {
  const status = getHapticFeedbackStatus();
  console.log('Auto-configuring haptic feedback:', status);
  return status.recommendedSettings;
};

export default hapticFeedback;